import type RFB from "@novnc/novnc";
import { useCallback, useEffect, useId, useRef, useState, type RefObject } from "react";
import { AgentCursors, type CursorAgent } from "./agent-cursors";
import { DesktopSwitcher } from "./desktop-switcher";
import type { MachineView } from "../../shared/contracts";
import { upsertMachine } from "../lib/agent";
import { api, errorText, wsOrigin } from "../lib/api";
import { invalidate } from "../lib/store";
import { pipelineUpdates, VNC_COMPRESSION, VNC_QUALITY } from "../lib/vnc-tuning";
import { IconEye, IconX } from "./icons";
import { Button, cx } from "./ui";

type State = "idle" | "connecting" | "connected" | "closed" | "error";

/** What the page's controls (keyboard, paste) drive on a connected screen held in control mode. */
export interface ScreenHandle {
  /** One X11 keysym: pressed then released, or only pressed/released with `down`. */
  sendKey(keysym: number, down?: boolean): void;
  /** Sets the computer's clipboard to `text`. */
  setClipboard(text: string): void;
}

/** A cloud computer usually serves its screen within a minute of being turned on: the start bar fills over that. */
const BOOT_EXPECTED_MS = 45_000;
/** A wake from the light sleep (MachineDO): a second or two. */
const WAKE_EXPECTED_MS = 2_000;

/** noVNC never gives up on a socket that opened without finishing the handshake: past this, the screen is unreachable. */
const CONNECT_TIMEOUT_MS = 25_000;
/** The noVNC module settles at load (it probes the browser's video decoder first): past this, it never will. */
const LOAD_TIMEOUT_MS = 20_000;
/** A computer that just started may not serve its screen yet: a failed handshake is retried this often, this far apart. */
const RETRIES = 2;
const RETRY_DELAY_MS = 3_000;

const UNREACHABLE = "L'écran ne répond pas pour l'instant (le bureau de l'ordinateur démarre peut-être) : réessaie dans quelques secondes.";
const CLOSED = "L'écran s'est fermé.";
const NO_PLAYER = "Le lecteur d'écran n'a pas pu se charger (l'app vient peut-être d'être mise à jour) : recharge la page.";

/** Why a cloud machine shows no screen, by state (null: it runs). */
export function screenUnavailableText(state: MachineView["state"]): string | null {
  switch (state) {
    case "running":
      return null;
    case "starting":
      return "L'ordinateur démarre : son écran s'affichera dès qu'il sera prêt.";
    case "sleeping":
      return "L'ordinateur est en veille : il se réveille en une seconde.";
    case "stopping":
      return "L'ordinateur est en train de s'arrêter.";
    case "unavailable":
      return "L'ordinateur cloud est momentanément indisponible : réessaie dans quelques minutes.";
    default:
      return "L'ordinateur est éteint (il s'arrête tout seul quand il ne sert plus) : allume-le pour voir son écran.";
  }
}

/**
 * The live screen of a cloud machine wherever the app shows it: the viewer while the machine runs; otherwise why there
 * is no screen and, when it is off, a button that turns it on and opens the screen as soon as it is up. `autoConnect`
 * (the /ordinateur?ecran=direct deep link): the screen opens by itself. `startOnce` (/ordinateur): an off computer is
 * turned on by itself, once per page visit (the page owns the flag: a remount never starts it again). `stage`: the
 * large rounded screen on the accent gradient of /ordinateur, held in view-only or control by the page (`viewOnly`).
 */
export function LiveScreen({
  machine,
  autoConnect = false,
  startOnce,
  stage = false,
  viewOnly,
  onReady,
  onClipboard,
  onMachine,
  agent,
  className,
}: {
  machine: MachineView;
  /** The agent working on this computer: its cursors on the screen (default: none drawn). */
  agent?: CursorAgent;
  /** Where a machine returned by the API goes (default: the machines list; an extra agent's page keeps its own). */
  onMachine?: (machine: MachineView) => void;
  autoConnect?: boolean;
  startOnce?: RefObject<boolean>;
  stage?: boolean;
  viewOnly?: boolean;
  onReady?: (handle: ScreenHandle | null) => void;
  onClipboard?: (text: string) => void;
  className?: string;
}) {
  const [wanted, setWanted] = useState(autoConnect);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);

  useEffect(() => {
    if (autoConnect) setWanted(true);
  }, [autoConnect]);

  const start = useCallback(async () => {
    setBusy(true);
    setError(null);
    setWanted(true);
    setStartedAt(Date.now());
    try {
      (onMachine ?? upsertMachine)(await api("POST /api/machines/:id/start", { params: { id: machine.id }, body: {} }));
    } catch (err) {
      setError(errorText(err));
      if (!onMachine) invalidate("machines");
    } finally {
      setBusy(false);
    }
  }, [machine.id, onMachine]);

  // Light sleep: a wake is instant (no boot): it never waits for the API, the machine comes back as « running ».
  const wake = useCallback(async () => {
    setError(null);
    setWanted(true);
    try {
      (onMachine ?? upsertMachine)(await api("POST /api/machines/:id/wake", { params: { id: machine.id }, body: { reason: "screen" } }));
    } catch (err) {
      setError(errorText(err));
      if (!onMachine) invalidate("machines");
    }
  }, [machine.id, onMachine]);

  useEffect(() => {
    if (!startOnce || startOnce.current) return;
    if (machine.state === "stopped") {
      startOnce.current = true;
      void start();
    } else if (machine.state === "sleeping") {
      // The page opened: the computer wakes ahead of the owner's (or his agent's) next move.
      startOnce.current = true;
      void wake();
    } else if (machine.state !== "unavailable") {
      // Found on (or on its way): nothing to start during this visit, even if it stops later.
      startOnce.current = true;
    }
  }, [machine.state, start, startOnce]);

  if (machine.state === "running") {
    return (
      <LiveDesktop
        machineId={machine.id}
        autoConnect={wanted}
        stage={stage}
        viewOnly={viewOnly}
        onReady={onReady}
        onClipboard={onClipboard}
        agent={agent}
        className={className}
      />
    );
  }

  const booting = machine.state === "starting" || (busy && machine.state === "stopped");
  return (
    <div className={cx("min-w-0", className)}>
      <div
        data-testid="live-desktop"
        data-state={machine.state}
        className={cx(
          "flex aspect-[16/10] w-full flex-col items-center justify-center gap-3 p-4 text-center",
          stage ? "screen-stage rounded-[18px] sm:rounded-[26px]" : "rounded-xl border border-line bg-[#111018]",
        )}
      >
        {booting ? (
          <BootProgress since={startedAt} phase={machine.bootPhase} />
        ) : (
          <p className="max-w-sm text-[13px] text-white/85">{screenUnavailableText(machine.state)}</p>
        )}
        {machine.state === "sleeping" ? (
          <Button size="sm" variant={stage ? "light" : "primary"} pill={stage} onClick={() => void wake()}>
            <IconEye width={16} height={16} />
            Réveiller et voir l'écran
          </Button>
        ) : null}
        {machine.state === "stopped" && !busy ? (
          <Button size="sm" variant={stage ? "light" : "primary"} pill={stage} onClick={() => void start()}>
            <IconEye width={16} height={16} />
            Allumer et voir l'écran
          </Button>
        ) : null}
        {error ? (
          <p role="alert" className="max-w-sm text-[12.5px] text-white/85">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** What the boot or wake is doing right now (MachineView.bootPhase), in the owner's words. */
const PHASE_TEXT: Record<NonNullable<MachineView["bootPhase"]>, string> = {
  start: "Démarrage de l'ordinateur…",
  home: "Récupération de ses fichiers…",
  ready: "Presque prêt…",
  wake: "Réveil…",
};

/**
 * « Démarrage de l'ordinateur… » (or its current step) with a bar that fills over the usual boot time (never quite
 * full until it is up); a wake from the light sleep fills it in about two seconds.
 */
function BootProgress({ since, phase }: { since: number | null; phase?: MachineView["bootPhase"] }) {
  const [origin] = useState(() => since ?? Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);
  const expected = phase === "wake" ? WAKE_EXPECTED_MS : BOOT_EXPECTED_MS;
  const ratio = 1 - Math.exp(-Math.max(0, now - origin) / (expected / 2.5));
  const percent = Math.min(95, Math.round(ratio * 100));
  return (
    <div className="flex w-full max-w-xs flex-col items-center gap-3">
      <p className="text-[14px] font-medium text-white" role="status">
        {phase ? PHASE_TEXT[phase] : PHASE_TEXT.start}
      </p>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-white/25"
        role="progressbar"
        aria-label="Démarrage de l'ordinateur"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <div className="h-full rounded-full bg-white transition-[width] duration-500" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

/**
 * Live view of a running cloud machine's desktop through noVNC (WebSocket /api/machines/<id>/vnc, session-checked by
 * the Worker). noVNC is loaded on demand. View-only by default: the agent may be working on that screen. A browser never
 * says why a WebSocket failed: after a failure, the machine's current state tells the user (off, starting…), and an
 * off machine makes LiveScreen show its start button instead. A controlled `viewOnly` (the page's « Prendre les
 * commandes ») replaces the « Lecture seule » switch; `onReady` hands the page what its keyboard and paste buttons
 * drive while connected (null otherwise), `onClipboard` the text the computer copies (sent only in control mode).
 */
export function LiveDesktop({
  machineId,
  defaultViewOnly = true,
  viewOnly: controlledViewOnly,
  autoConnect = false,
  stage = false,
  onReady,
  onClipboard,
  agent,
  followRunId,
  className,
}: {
  machineId: string;
  agent?: CursorAgent;
  /** Show the desktop this mission run works on (a run page): picked as soon as the computer reports it. */
  followRunId?: string;
  defaultViewOnly?: boolean;
  viewOnly?: boolean;
  autoConnect?: boolean;
  stage?: boolean;
  onReady?: (handle: ScreenHandle | null) => void;
  onClipboard?: (text: string) => void;
  className?: string;
}) {
  const screenRef = useRef<HTMLDivElement>(null);
  const rfbRef = useRef<RFB | null>(null);
  const generation = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retries = useRef(0);
  const connectRef = useRef<() => Promise<void>>(async () => undefined);
  const [state, setState] = useState<State>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [reload, setReload] = useState(false);
  const [ownViewOnly, setViewOnly] = useState(defaultViewOnly);
  const viewOnly = controlledViewOnly ?? ownViewOnly;
  const viewOnlyRef = useRef(viewOnly);
  const toggleId = useId();
  // Which desktop of the cloud computer is shown (DesktopSwitcher); read by connect() through the ref.
  const [desktop, setDesktop] = useState(0);
  const desktopRef = useRef(0);
  const followed = useRef(false);
  useEffect(() => {
    if (!followRunId || followed.current) return;
    let alive = true;
    const find = () =>
      api("GET /api/machines/:id/desktops", { params: { id: machineId } }).then(
        (v) => {
          const d = v.desktops.find((x) => x.runId === followRunId)?.index;
          if (!alive || d === undefined || followed.current) return;
          followed.current = true;
          if (d === desktopRef.current) return;
          desktopRef.current = d;
          setDesktop(d);
          if (rfbRef.current) {
            retries.current = 0;
            void connectRef.current();
          }
        },
        () => undefined,
      );
    void find();
    // The run takes its desktop a few seconds after it starts: look again until it shows up.
    const t = setInterval(() => (followed.current ? clearInterval(t) : void find()), 4_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [followRunId, machineId]);
  const onReadyRef = useRef(onReady);
  const onClipboardRef = useRef(onClipboard);
  useEffect(() => {
    onReadyRef.current = onReady;
    onClipboardRef.current = onClipboard;
  }, [onReady, onClipboard]);

  const clearTimer = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const disconnect = useCallback(() => {
    generation.current += 1;
    clearTimer();
    const rfb = rfbRef.current;
    rfbRef.current = null;
    onReadyRef.current?.(null);
    if (rfb) {
      try {
        rfb.disconnect();
      } catch {
        // already closed
      }
    }
  }, [clearTimer]);

  /**
   * After a failure: the machine's current state explains it (it refreshes the page's machine list too). `retry`: a
   * handshake that failed while the machine runs is tried again a few times first (a desktop not serving yet).
   */
  const explain = useCallback(
    async (gen: number, fallback: string, retry = false) => {
      let fresh: MachineView | undefined;
      try {
        fresh = (await api("GET /api/machines")).machines.find((m) => m.id === machineId);
      } catch {
        // keep the fallback (a lost session sends the app back to the sign-in page)
      }
      if (gen !== generation.current) return;
      if (fresh) upsertMachine(fresh);
      if (retry && fresh?.state === "running" && retries.current < RETRIES) {
        retries.current += 1;
        setTimeout(() => {
          if (gen === generation.current) void connectRef.current();
        }, RETRY_DELAY_MS);
        return;
      }
      setState("error");
      setMessage((fresh && screenUnavailableText(fresh.state)) || fallback);
    },
    [machineId],
  );

  const connect = useCallback(async () => {
    disconnect();
    const gen = generation.current;
    setState("connecting");
    setMessage(null);
    setReload(false);
    let RfbClass: typeof RFB | null;
    try {
      RfbClass = await Promise.race([
        import("@novnc/novnc").then((m) => m.default),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), LOAD_TIMEOUT_MS)),
      ]);
    } catch {
      RfbClass = null;
    }
    if (gen !== generation.current) return;
    if (!RfbClass) {
      setState("error");
      setMessage(NO_PLAYER);
      setReload(true);
      return;
    }
    const target = screenRef.current;
    if (!target) return;
    target.replaceChildren();
    let rfb: RFB;
    try {
      const desk = desktopRef.current > 0 ? `?desktop=${desktopRef.current}` : "";
      rfb = new RfbClass(target, `${wsOrigin()}/api/machines/${encodeURIComponent(machineId)}/vnc${desk}`, { shared: true });
    } catch {
      void explain(gen, UNREACHABLE);
      return;
    }
    rfb.viewOnly = viewOnlyRef.current;
    rfb.scaleViewport = true;
    rfb.resizeSession = false;
    rfb.focusOnClick = !viewOnlyRef.current;
    rfb.background = "transparent";
    rfb.qualityLevel = VNC_QUALITY;
    rfb.compressionLevel = VNC_COMPRESSION;
    // The next screen update is asked for while the current one arrives: the refresh rate no longer waits on the round
    // trip to the computer (lib/vnc-tuning.ts).
    pipelineUpdates(rfb, RfbClass);
    let opened = false;
    rfb.addEventListener("connect", () => {
      if (gen !== generation.current) return;
      clearTimer();
      opened = true;
      retries.current = 0;
      setState("connected");
      onReadyRef.current?.({
        sendKey: (keysym, down) => rfb.sendKey(keysym, null, down),
        setClipboard: (text) => rfb.clipboardPasteFrom(text),
      });
    });
    rfb.addEventListener("clipboard", (e) => {
      if (gen !== generation.current) return;
      onClipboardRef.current?.(e.detail.text);
    });
    rfb.addEventListener("disconnect", (e) => {
      if (gen !== generation.current) return;
      clearTimer();
      rfbRef.current = null;
      onReadyRef.current?.(null);
      // Never closed by the user here (Fermer l'écran bumps the generation): a failed handshake (computer off, desktop
      // not up yet, session lost) or the computer stopping under the viewer.
      void explain(gen, e.detail.clean ? CLOSED : UNREACHABLE, !opened);
    });
    rfb.addEventListener("credentialsrequired", () => {
      if (gen !== generation.current) return;
      setState("error");
      setMessage("L'écran demande un mot de passe : il n'est pas accessible depuis l'app.");
      disconnect();
    });
    rfb.addEventListener("securityfailure", () => {
      if (gen !== generation.current) return;
      setState("error");
      setMessage("Connexion à l'écran refusée.");
      disconnect();
    });
    rfbRef.current = rfb;
    timer.current = setTimeout(() => {
      if (gen !== generation.current) return;
      disconnect();
      void explain(generation.current, UNREACHABLE);
    }, CONNECT_TIMEOUT_MS);
  }, [clearTimer, disconnect, explain, machineId]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  /** Opened by the user or by the page (not a retry): a fresh round of retries. */
  const open = useCallback(() => {
    retries.current = 0;
    void connect();
  }, [connect]);

  useEffect(() => {
    viewOnlyRef.current = viewOnly;
    const rfb = rfbRef.current;
    if (rfb) {
      rfb.viewOnly = viewOnly;
      rfb.focusOnClick = !viewOnly;
    }
  }, [viewOnly]);

  useEffect(() => {
    if (autoConnect) open();
    return () => disconnect();
  }, [autoConnect, open, disconnect]);

  const active = state === "connecting" || state === "connected";

  return (
    <div className={cx("min-w-0", className)}>
      <div
        data-testid="live-desktop"
        data-state={state}
        data-view-only={viewOnly ? "true" : "false"}
        className={cx(
          "relative aspect-[16/10] w-full overflow-hidden",
          stage ? "screen-stage rounded-[18px] shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)] sm:rounded-[26px]" : "rounded-xl border border-line bg-[#111018]",
        )}
      >
        <div ref={screenRef} className="vnc-screen absolute inset-0" aria-label="Écran de la machine" />
        {/* Where the agents act on this desktop (components/agent-cursors.tsx); grey while the owner drives. */}
        {agent ? <AgentCursors machineId={machineId} desktop={desktop} agent={agent} live={state === "connected"} dimmed={!viewOnly} /> : null}
        {!active ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 text-center">
            {message ? (
              <p role={state === "error" ? "alert" : undefined} className="max-w-sm text-[13px] text-white/80">
                {message}
              </p>
            ) : null}
            {reload ? (
              <Button size="sm" onClick={() => location.reload()}>
                Recharger la page
              </Button>
            ) : (
              <Button size="sm" variant={stage ? "light" : "primary"} pill={stage} onClick={open}>
                <IconEye width={16} height={16} />
                {state === "idle" ? "Voir l'écran" : "Revoir l'écran"}
              </Button>
            )}
          </div>
        ) : state === "connecting" ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[13px] text-white/85">Connexion à l'écran…</div>
        ) : null}
      </div>
      {/* Several desktops: the switcher (hidden by itself with a single desktop). On /ordinateur the controls bar under
          the screen covers the whole computer: « Prendre les commandes » pauses the agent on every desktop, and the
          keyboard and paste controls drive the desktop shown here. */}
      <DesktopSwitcher
        machineId={machineId}
        value={desktop}
        dark={stage}
        className={stage ? "mt-3 justify-center" : "mt-2"}
        onChange={(d) => {
          if (d === desktopRef.current) return;
          desktopRef.current = d;
          setDesktop(d);
          if (rfbRef.current || state === "connecting") open();
        }}
      />
      {controlledViewOnly === undefined || (active && !stage) ? (
        <div className={cx("mt-2 flex flex-wrap items-center gap-3", controlledViewOnly === undefined ? "justify-between" : "justify-end")}>
          {controlledViewOnly === undefined ? (
            <label htmlFor={toggleId} className="flex items-center gap-2 text-[13px] text-muted">
              <input id={toggleId} type="checkbox" className="switch" checked={viewOnly} onChange={(e) => setViewOnly(e.target.checked)} />
              Lecture seule
            </label>
          ) : null}
          {active && !stage ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                disconnect();
                setState("closed");
                setMessage(null);
              }}
            >
              <IconX width={14} height={14} />
              Fermer l'écran
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
